import { ArrowForward } from '@mui/icons-material'
import {
  Alert,
  Button,
  Divider,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState, useContext, useRef } from 'react'
import { salesOrderNoteType } from '../types'
import axios from 'axios'
import dayjs from 'dayjs'
import './SalesOrderNotes.css'
import { dataContext } from '..'
import ErrorScreen from '../Misc/ErrorScreen'

function SalesOrderNotes(props: {
  salesOrderId: string | null
  objectType: 'salesOrder' | 'rma'
  setNotesOpen: (open: boolean) => void
  noteDraft?: string
  setNoteDraft?: (draft: string) => void
}) {
  const { user } = useContext(dataContext)

  const [notes, setNotes] = useState<salesOrderNoteType[]>([])
  const [newNote, setNewNote] = useState(props.noteDraft || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const newNodeId = useRef(0)

  useEffect(() => {
    if (props.salesOrderId) {
      let loading = true
      setTimeout(() => {
        setLoading(loading)
      }, 200)

      axios
        .get(`/${props.objectType}/${props.salesOrderId}/notes`)
        .then(results => {
          setNotes(results.data)
          setError(null)
        })
        .catch(err => {
          setError('Failed to load notes. Please try again or contact support if the error persists.')
          console.error(err)
        })
        .finally(() => {
          loading = false
          setLoading(false)
        })
    }
  }, [props.salesOrderId])

  if (error) {
    return <ErrorScreen errorText={error} isDrawer />
  }

  return (
    <div className="sales-order-notes">
      <div>
        <div className="sales-order-notes-header">
          <div>
            <Typography variant="h6">Notes</Typography>
            <IconButton onClick={() => props.setNotesOpen(false)}>
              <ArrowForward />
            </IconButton>
          </div>
          <Divider />
        </div>
        <TableContainer className="sales-order-notes-table">
          <Table color="secondary">
            <TableHead>
              <TableRow>
                <TableCell>Date and Time</TableCell>
                <TableCell>User</TableCell>
                {props.objectType === 'salesOrder' && <TableCell>Title</TableCell>}
                <TableCell width="50%">Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 2 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 4 }).map((_, index) => (
                        <TableCell key={index}>
                          <Skeleton />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : notes.map(note => (
                    <TableRow key={note.note_id}>
                      <TableCell>{dayjs(note.last_update_timestamp).format('MM/DD/YYYY hh:mm A')}</TableCell>
                      <TableCell>{note.user_name}</TableCell>
                      {props.objectType === 'salesOrder' && <TableCell>{note.title}</TableCell>}
                      <TableCell>{note.note?.replaceAll('<br />', '') ?? ''}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className="sales-order-notes-footer">
        <Divider />
        {user.user_type !== 'Office Staff' && user.user_type !== 'Sales Rep' && (
          <div>
            {loading ? (
              <Skeleton width="100%" height="100%">
                <TextField multiline rows={6} />
              </Skeleton>
            ) : (
              <TextField
                label="Add Note"
                multiline
                rows={6}
                fullWidth
                slotProps={{ htmlInput: { maxLength: 4096 } }}
                onChange={e => {
                  setNewNote(e.target.value)
                  props.setNoteDraft?.(e.target.value)
                  setSaveError(null) // Clear error when user starts typing
                }}
                value={newNote}
              />
            )}
            <div className="sales-order-notes-button-container">
              {saveError && <Alert severity="error">{saveError}</Alert>}
              <Button
                variant="contained"
                disabled={newNote.length === 0}
                onClick={() => {
                  axios
                    .post(`/${props.objectType}/${props.salesOrderId}/notes/add`, {
                      note: newNote,
                      userId: user.user_id,
                    })
                    .then(() => {
                      setNotes([
                        {
                          note_id: `new-${newNodeId.current++}`,
                          last_update_timestamp: new Date(),
                          user_name: user.display_name,
                          title: 'Update',
                          note: newNote,
                        } as salesOrderNoteType,
                        ...notes,
                      ])
                      setNewNote('')
                      props.setNoteDraft?.('')
                      setError(null)
                      setSaveError(null)
                    })
                    .catch(err => {
                      setSaveError(
                        'An error occurred while saving your note. Please try again or contact support if the error persists.',
                      )
                      console.error(err)
                    })
                }}
              >
                {loading ? <Skeleton /> : 'Save and Add Note'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SalesOrderNotes
